import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Mail, Heart, Scale, Users, Clock, DollarSign, Shield, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEOHead from '@/components/SEOHead';

const WrongfulDeathPage = () => {
  // Add Service schema for SEO via SEOHead component
  const serviceData = {
    name: "Wrongful Death Legal Representation",
    description: "Compassionate legal representation for families who have lost loved ones due to negligence, helping secure compensation for funeral expenses, lost income, and pain and suffering.",
    category: "Wrongful Death Law"
  };

  // HowTo schema for wrongful death claims
  const howToSteps = {
    name: "How to File a Wrongful Death Claim in Washington State",
    description: "Steps for families who have lost a loved one due to negligence to pursue a wrongful death lawsuit and secure compensation.",
    totalTime: "PT60M",
    steps: [
      { name: "Determine Who Can File the Claim", text: "In Washington, the personal representative of the deceased's estate must file the claim. This is typically named in the will or appointed by the court." },
      { name: "Identify the Cause of Death", text: "Determine if negligence, recklessness, or wrongful conduct caused the death. Common causes include car crashes, workplace accidents, medical malpractice, and defective products." },
      { name: "Gather Evidence and Documentation", text: "Collect death certificates, medical records, accident reports, witness statements, and any evidence of the responsible party's negligence." },
      { name: "Understand the Statute of Limitations", text: "Washington has a 3-year statute of limitations for wrongful death claims, starting from the date of death. Acting promptly is critical." },
      { name: "Calculate Damages", text: "Damages may include funeral and burial costs, lost income and benefits, medical bills before death, loss of companionship, and pain suffered before death." },
      { name: "Contact a Wrongful Death Attorney", text: "An experienced wrongful death attorney can guide you through this difficult process with compassion while fighting for maximum compensation." },
      { name: "File the Wrongful Death Lawsuit", text: "Your attorney will file the lawsuit, negotiate with insurance companies, and if necessary, take the case to trial to secure justice for your family." }
    ]
  };

  // Article schema for SEO
  React.useEffect(() => {
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Wrongful Death Attorney in Washington State - Amy C. Brown Law",
      "description": "Experienced wrongful death attorney serving families across Washington State. Personal experience with wrongful death. Free consultation. No fees unless you win.",
      "author": {
        "@type": "Person",
        "name": "Amy C. Brown"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Amy C. Brown Law"
      },
      "datePublished": "2026-02-17",
      "dateModified": "2026-02-17"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'wrongful-death-article');
    script.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[data-schema="wrongful-death-article"]');
      if (existingScript) existingScript.remove();
    };
  }, []);

  const commonCauses = [
    'Car, truck, or motorcycle crashes',
    'Maritime or work-related accidents (including Jones Act cases)',
    'Slip and fall or premises liability incidents',
    'Medical malpractice or nursing home neglect',
    'First responder or on-duty pursuit injuries'
  ];

  const eligibleBeneficiaries = [
    'Surviving spouse or state-registered domestic partner',
    'Children (including stepchildren in some cases)',
    'Parents (if there is no surviving spouse/domestic partner or children)',
    'Siblings (in limited circumstances)'
  ];

  const recoverableDamages = [
    'Medical and funeral expenses',
    'Lost financial support (wages, benefits, pension the deceased would have provided)',
    'Loss of companionship, guidance, and care',
    'Loss of household services',
    'Pain and suffering of surviving family members',
    'Emotional distress'
  ];

  // FAQ data for schema (plain text for JSON-LD)
  const faqSchemaData = [
    {
      question: 'How long do I have to file a wrongful death claim in Washington?',
      answer: "The statute of limitations is generally three years from the date of death (RCW 4.16.080). Some exceptions apply (e.g., delayed discovery of fault). Acting quickly preserves evidence and strengthens your case — I recommend contacting me as soon as possible."
    },
    {
      question: 'What if the deceased was partly at fault?',
      answer: "Washington uses pure comparative negligence — compensation can still be recovered even if the deceased was partially at fault (reduced by their percentage). I fight to minimize any fault arguments and maximize what your family receives."
    },
    {
      question: 'Can I still recover if workers\' compensation is involved?',
      answer: "Yes — workers' comp may cover some benefits, but a wrongful death claim against a third party (e.g., negligent driver, manufacturer) can provide additional recovery for pain/suffering and other losses not covered by comp. I sort out overlapping claims carefully."
    },
    {
      question: 'How long does a wrongful death case take?',
      answer: "Most settle within 12–24 months, but complex cases (e.g., disputed liability or multiple parties) can take longer. I aim for fair, timely resolutions while preparing every case as if it will go to trial."
    },
    {
      question: 'Do I need to go to court?',
      answer: "Most wrongful death cases settle out of court. I negotiate aggressively with insurance companies and defendants to reach a fair resolution without a trial, but I'm fully prepared to litigate if needed."
    }
  ];

  // FAQ data for display (with JSX for links)
  const faqs = [
    {
      question: 'How long do I have to file a wrongful death claim in Washington?',
      answer: (<span>The statute of limitations is generally three years from the date of death (<a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=4.16.080" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-800">RCW 4.16.080</a>). Some exceptions apply (e.g., delayed discovery of fault). Acting quickly preserves evidence and strengthens your case — I recommend contacting me as soon as possible.</span>)
    },
    {
      question: 'What if the deceased was partly at fault?',
      answer: "Washington uses pure comparative negligence — compensation can still be recovered even if the deceased was partially at fault (reduced by their percentage). I fight to minimize any fault arguments and maximize what your family receives."
    },
    {
      question: 'Can I still recover if workers\' compensation is involved?',
      answer: "Yes — workers' comp may cover some benefits, but a wrongful death claim against a third party (e.g., negligent driver, manufacturer) can provide additional recovery for pain/suffering and other losses not covered by comp. I sort out overlapping claims carefully."
    },
    {
      question: 'How long does a wrongful death case take?',
      answer: "Most settle within 12–24 months, but complex cases (e.g., disputed liability or multiple parties) can take longer. I aim for fair, timely resolutions while preparing every case as if it will go to trial."
    },
    {
      question: 'Do I need to go to court?',
      answer: "Most wrongful death cases settle out of court. I negotiate aggressively with insurance companies and defendants to reach a fair resolution without a trial, but I'm fully prepared to litigate if needed."
    }
  ];

  const whyChooseMe = [
    'Direct, one-on-one communication — you always speak to me',
    'Over 10 years of experience in personal injury and wrongful death',
    'Defense-side background — I know how insurers think and how to counter them',
    'Contingency fees — no financial risk to your family',
    'Compassionate, strategic representation focused on your healing'
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="wrongful-death-page">
      <SEOHead 
        title="Wrongful Death Attorney | Washington State | Amy C. Brown Law"
        description="Compassionate wrongful death attorney helping families in Washington State. Personal experience with loss. Fighting for fair compensation. Free consultation."
        serviceData={serviceData}
        howToData={howToSteps}
        faqData={faqSchemaData}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              Washington State
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Wrongful Death
            </h1>
            <p className="text-xl text-primary mb-4">
              Supporting Families Through Unthinkable Loss
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Losing a loved one due to someone else's negligence or wrongful act is devastating. The grief is overwhelming, and the sudden financial and emotional burdens can feel impossible to bear.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="wrongful-death-hero-call"
              >
                <Phone className="w-5 h-5" />
                (425) 675-1224
              </a>
              <a
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Connection Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-2xl p-8">
            <Heart className="w-10 h-10 text-teal-600 mb-4" />
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              <strong>I have been personally involved with wrongful death in my own family</strong> — multiple members were killed in a construction-related incident. I vividly remember that horrible day and the days after. I know the pain you're going through, and I understand the questions and fears you have right now.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              As an Everett wrongful death attorney serving families across Washington State — including Seattle, Tacoma, Spokane, Vancouver, Bellevue, Renton, Kent, Federal Way, Spokane Valley, and beyond — I work directly with you. No paralegals or case managers. I handle the legal complexities so you can focus on healing and remembering your loved one.
            </p>
          </div>
        </div>
      </section>

      {/* What Is a Wrongful Death Claim */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                What Is a Wrongful Death Claim in Washington?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                A wrongful death claim is a civil lawsuit filed when someone's death is caused by another person's negligence, recklessness, or intentional act. In Washington, these claims are governed by <a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=4.20.010" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-800 font-medium">RCW 4.20.010</a> and allow the personal representative of the deceased's estate to seek compensation for the survivors' losses.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                I help families pursue justice when these tragedies occur, whether the at-fault party is an individual, employer, or company.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">Common Causes I See Include:</h3>
              <ul className="space-y-3">
                {commonCauses.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-1" />
                    <span className="text-slate-600">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can File */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Who Can File a Wrongful Death Claim?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                In Washington, only the <strong>personal representative of the deceased's estate</strong> — often named in a will or appointed by the court — can file the claim.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                I guide families through this process — from appointing a representative to ensuring fair distribution of any recovery.
              </p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <Users className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">Eligible Beneficiaries:</h3>
              <ul className="space-y-3">
                {eligibleBeneficiaries.map((beneficiary, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-teal-500 flex-shrink-0 mt-1" />
                    <span className="text-slate-600">{beneficiary}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What Compensation */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              What Compensation Can Families Recover?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Wrongful death damages in Washington aim to compensate for both economic and non-economic losses.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {recoverableDamages.map((damage, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <DollarSign className="w-8 h-8 text-teal-500 mb-3" />
                <p className="text-slate-700 font-medium">{damage}</p>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 max-w-4xl mx-auto">
            <AlertCircle className="w-8 h-8 text-amber-600 mb-4" />
            <p className="text-slate-700 leading-relaxed">
              There is no fixed "average" settlement — values range from tens of thousands to millions depending on the deceased's age, earning capacity, family relationships, and the strength of liability evidence. Insurance limits and available assets also play a role. <strong>I evaluate each case individually during a free consultation to give you a realistic picture.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
                No Upfront Costs
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                How Much Does a Wrongful Death Case Cost?
              </h2>
              <p className="text-lg text-teal-100 leading-relaxed mb-6">
                I handle wrongful death cases on a <strong>contingency fee basis</strong> — you pay nothing upfront. My fee (typically 33–40%, depending on case complexity) is only taken from the recovery if we succeed.
              </p>
              <p className="text-lg text-teal-100 leading-relaxed">
                Case expenses (e.g., filing fees, experts) are advanced by me and reimbursed only from the settlement if we win. This structure lets families focus on grief and healing without added financial pressure.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <DollarSign className="w-10 h-10 text-teal-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">$0 Upfront</h4>
                <p className="text-sm text-teal-200">No retainers or hourly fees</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-teal-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">No Win, No Fee</h4>
                <p className="text-sm text-teal-200">You only pay if I succeed</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Scale className="w-10 h-10 text-teal-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">33-40%</h4>
                <p className="text-sm text-teal-200">Typical contingency fee</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-teal-300 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Costs Advanced</h4>
                <p className="text-sm text-teal-200">I cover expenses until we win</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Common Questions Families Ask
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl px-6 border-none"
              >
                <AccordionTrigger className="text-left font-semibold text-secondary hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why Choose Me */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Why Choose Me for Your Wrongful Death Case?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                I understand the pain of sudden loss on a personal level. I've been through it, and I know the emotional weight families carry. As a solo attorney, I offer:
              </p>
              <ul className="space-y-4">
                {whyChooseMe.map((reason, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                If you've lost a loved one due to someone else's actions, I'm here to help protect your family's rights and seek the justice and compensation you deserve.
              </p>
              <p className="text-slate-600 mb-6">
                Call/text me directly at <strong>(425) 675-1224</strong> or email <strong>amy@amycbrownlaw.com</strong> for a free, no-pressure consultation. I respond personally within 1 business day.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-4 rounded-full font-semibold hover:bg-teal-700 transition-all"
                  data-testid="wrongful-death-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  Call/Text Now: (425) 675-1224
                </a>
                <a
                  href="mailto:amy@amycbrownlaw.com"
                  className="inline-flex items-center justify-center gap-2 border-2 border-teal-600 text-teal-600 px-6 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  Email: amy@amycbrownlaw.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <p className="text-sm text-slate-500 text-center italic">
            This is general information only — not legal advice. Wrongful death laws are complex and time-sensitive. Contact me to discuss your specific situation.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation – No Obligation
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            I'm here to help your family through this difficult time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-600 transition-all shadow-xl"
              data-testid="wrongful-death-final-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call/Text Me Now: (425) 675-1224
            </a>
            <a
              href="mailto:amy@amycbrownlaw.com"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all"
            >
              <Mail className="w-6 h-6" />
              Email Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WrongfulDeathPage;
